import {
  MediaDeleteModal,
  MediaDeleteModalRef,
} from '@components/MediaDeleteModal/MediaDeleteModal';
import { mediaDeletedEvent } from '@lib/events/eventBus';
import { billingStore } from '@stores/Billing';
import { notificationStore } from '@stores/Notifications';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { userStore } from '@stores/User';
import { observer } from 'mobx-react';
import { RefObject, useEffect } from 'react';

/**
 * Hosts MediaDeleteModal for the panel's "Delete batch" action. Mounted only
 * while a batch's delete confirmation is open — keeps the notification lookup
 * + items mapping out of the parent's render path, and observer-wraps the
 * read so MobX picks up any title / mediaIds changes that happen while the
 * modal is on screen.
 *
 * Items are synthesized from the notification's mediaIds + mediaTitles
 * (full Media objects aren't available outside library context), so the
 * "N hours of storage freed" line shows 0 — acceptable since the batch was
 * just created and the user knows what they're deleting.
 */
export const DeleteBatchModalHost = observer(function DeleteBatchModalHost({
  notificationId,
  modalRef,
  onClose,
}: {
  notificationId: string;
  modalRef: RefObject<MediaDeleteModalRef | null>;
  onClose: () => void;
}) {
  const uploadManager = useUploadManager();

  useEffect(() => {
    if (modalRef.current) modalRef.current.open();
  }, [modalRef]);

  const notif = notificationStore.notifications.find((n) => n.id === notificationId);
  if (!notif || (notif.type !== 'upload-file' && notif.type !== 'add-media-via-url')) {
    return null;
  }

  return (
    <MediaDeleteModal
      ref={modalRef}
      items={notif.mediaIds.map((id, i) => ({
        id,
        // `||` not `??` so empty strings fall back too — the pair can drift
        // if the Results modal pruned mediaIds without also pruning mediaTitles.
        name: notif.mediaTitles[i] || 'Untitled',
      }))}
      libraryId={notif.libraryId}
      libraryName={notif.libraryName}
      onConfirmDelete={async (mediaIds) => {
        // Bulk-delete endpoint directly — we're not in a folder context.
        // Emit mediaDeletedEvent so any mounted folder prunes its local
        // data.files immediately (without this the grid waits on the
        // status-poll cycle to notice each 404).
        await userStore.apiWithAuth(`/library/${notif.libraryId}/media/delete/bulk`, {
          method: 'POST',
          body: { mediaIds },
        });
        mediaDeletedEvent.emit({ libraryId: notif.libraryId, mediaIds });
        uploadManager.refreshLibrary(notif.libraryId);
        // Drop the notification so the panel doesn't keep polling for media
        // that no longer exist.
        notificationStore.remove(notif.id);
        // Mirror LibraryFolderStore.deleteBulkMedia's delayed billing
        // refresh — the storage counter would otherwise stay stale until
        // some other event refreshes it. 10s wait matches the folder path
        // (deletion takes a moment to reflect in the user's storage).
        setTimeout(() => {
          billingStore.refetchCreditsAfterOperation().catch((e) => {
            console.error('Error refreshing credits after deleting batch:', e);
          });
        }, 10_000);
      }}
      onClose={onClose}
    />
  );
});
